import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Heart, 
  ArrowLeft, 
  Film, 
  MapPin, 
  Calendar, 
  Users, 
  Clock,
  Award,
  Play,
  Quote
} from "lucide-react";
import sweetReunionPoster from "@/assets/sweet-reunion-poster.jpeg";

const TheFilm = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-story-sudan/5 to-story-germany/10">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Button 
            className="bg-story-hope hover:bg-story-hope/90 text-black font-bold"
            onClick={() => window.location.href = '/#fund-project'}
          >
            <Heart className="w-4 h-4 mr-2" />
            Support the Film
          </Button>
        </div>
      </nav>

      {/* Hero Section with Poster */}
      <section className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-7xl mx-auto">
            {/* Poster */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img
                  src={sweetReunionPoster}
                  alt="A Sweet Reunion - 26 Years Apart Documentary Poster"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-8">
                  <Button size="lg" className="bg-white/90 text-black hover:bg-white gap-2">
                    <Play className="w-5 h-5" />
                    Watch Trailer
                  </Button>
                </div>
              </div>
              
              {/* Floating badges */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 }}
                className="absolute -top-4 -right-4 bg-story-hope text-black font-bold text-sm px-4 py-2 rounded-full shadow-lg"
              >
                🎬 In Production
              </motion.div>
            </motion.div>

            {/* Film Details */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Film className="w-5 h-5 text-story-hope" />
                  <span className="text-story-hope font-semibold text-sm uppercase tracking-wide">Documentary Feature</span>
                </div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-4">
                  A Sweet Reunion
                </h1>
                <p className="text-2xl md:text-3xl text-story-hope font-serif">
                  26 Years Apart
                </p>
              </div>

              <p className="text-xl text-muted-foreground leading-relaxed">
                A powerful documentary following the miraculous reunion between Tachen, a Sudanese refugee who overcame 
                impossible odds, and Bettina, the young German social worker who helped him learn to walk 26 years ago.
              </p>

              {/* Film Info */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Clock className="w-5 h-5 text-story-peace" />
                    <div>
                      <p className="text-sm text-muted-foreground">Runtime</p>
                      <p className="font-semibold">90 Minutes</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-story-hope" />
                    <div>
                      <p className="text-sm text-muted-foreground">Release</p>
                      <p className="font-semibold">2025</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-story-sudan" />
                    <div>
                      <p className="text-sm text-muted-foreground">Locations</p>
                      <p className="font-semibold">Sudan, Germany, USA</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Award className="w-5 h-5 text-story-germany" />
                    <div>
                      <p className="text-sm text-muted-foreground">Director</p>
                      <p className="font-semibold">VadoFilms</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Documentary</Badge>
                <Badge variant="secondary">True Story</Badge>
                <Badge variant="secondary">Reunion</Badge>
                <Badge variant="secondary">Refugee Story</Badge>
                <Badge variant="secondary">Hope</Badge>
                <Badge variant="secondary">Healing</Badge>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Synopsis Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-8 text-center">
              The Story
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                In 1993, a young boy named Tachen arrived at Friedensdorf Peace Village in Germany from war-torn Sudan. 
                Unable to walk and far from home, he was scared, alone, and uncertain of his future. There, he met Bettina—a 
                young social worker on her very first job assignment.
              </p>
              <p>
                What happened next was nothing short of a miracle. Through months of dedicated care, physical therapy, 
                and unwavering belief, Tachen learned to walk. More than that, he learned to hope. The bond between 
                Tachen and Bettina transcended language, culture, and circumstance.
              </p>
              <p>
                When Tachen was healed enough to return home, they said goodbye—never knowing if they would see each other again.
              </p>
              <p className="text-story-hope font-medium text-xl">
                For 26 years, both carried memories of each other. Bettina searched for her "lost boy" countless times. 
                Tachen wondered if she ever thought of him.
              </p>
              <p>
                On January 22, 2020, after a trauma workshop, Bettina searched one more time. And there he was—his face 
                unchanged, immediately recognizable. The message she sent would change both their lives forever.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <Quote className="w-12 h-12 text-story-hope mx-auto mb-6" />
            <blockquote className="font-serif text-2xl md:text-3xl text-foreground leading-relaxed mb-6">
              "She didn't just help me walk. She helped me believe that my life wasn't over—it was just beginning."
            </blockquote>
            <cite className="text-muted-foreground text-lg">— Tachen, 2024</cite>
          </motion.div>
        </div>
      </section>

      {/* Featured People */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Users className="w-5 h-5 text-story-hope" />
              <span className="text-story-hope font-semibold text-sm uppercase tracking-wide">Featured</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              The People Behind the Story
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-card/50 border-story-sudan/30">
                <CardContent className="p-8">
                  <h3 className="font-serif text-2xl text-foreground mb-2">Tachen</h3>
                  <p className="text-story-sudan font-medium mb-4">Survivor • Motivational Speaker</p>
                  <p className="text-muted-foreground leading-relaxed">
                    A Sudanese refugee who arrived at Peace Village as a child unable to walk due to a congenital condition. 
                    Against all odds, he healed, returned home, and built a life. Now he shares his story to inspire others 
                    that healing is possible and hope endures.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full bg-card/50 border-story-germany/30">
                <CardContent className="p-8">
                  <h3 className="font-serif text-2xl text-foreground mb-2">Bettina</h3>
                  <p className="text-story-germany font-medium mb-4">Social Worker • Humanitarian</p>
                  <p className="text-muted-foreground leading-relaxed">
                    A young German woman on her first social work assignment at Friedensdorf Peace Village. Her dedication 
                    to Tachen's care planted a seed that would define her entire career helping displaced and injured 
                    children from around the world.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <Heart className="w-12 h-12 text-story-hope mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Help Complete This Miracle
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Your support will bring Tachen back to Friedensdorf Peace Village—the place where his life changed forever. 
              Together, they'll inspire the children there today, showing them that healing is real and miracles do happen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-story-hope hover:bg-story-hope/90 text-black font-bold text-lg px-8 py-4 h-auto shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                onClick={() => window.location.href = '/#fund-project'}
              >
                <Heart className="w-5 h-5 mr-2" />
                Back This Project
              </Button>
              <Link to="/">
                <Button 
                  size="lg"
                  variant="outline"
                  className="font-semibold text-lg px-8 py-4 h-auto"
                >
                  Learn More
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border/50">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© 2024 A Sweet Reunion Documentary. Directed by VadoFilms.</p>
        </div>
      </footer>
    </div>
  );
};

export default TheFilm;
